import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import Layout from "../../layout/Layout";
import { compareMakinaExcelDebug } from "../../../api/uretim/makineDataApi";

const DEFAULT_EXCEL_HEADERS = [
  "FORM NO",
  "İŞ EMRİ NO",
  "GÜN",
  "AY",
  "TARİH",
  "VRD",
  "OPERATÖR",
  "MALZEME CİNSİ",
  "KALINLIK",
  "EN1",
  "EN2",
  "BOY",
  "BANT ENİ",
  "BAĞ İÇİ",
  "FİRMA",
  "HEDEF HIZ",
  "HEDEF PAKET DOLUM SÜRESİ",
  "KG/M FORMUL",
  "HEDEF ÜRETİM FORMUL",
  "HIZ FORMUL",
  "TESTERE KIRILMASI",
  "GERÇEKLEŞEN HIZ",
  "PAKET DOLUM SÜRESİ FORMUL",
  "MAMUL BAĞ ADETİ",
  "MAMUL YARIM BAĞ",
  "MAMUL MİKTARI KG",
  "METRAJ FORMUL",
  "ALTERNATİF BAĞ ADETİ",
  "ALTERNATİF YARIM BAĞ",
  "ALTERNATİF KG",
  "ALTERNATİF YÜZDE FORMUL",
  "ALTERNATİF METRAJ FORMUL",
  "ÜRETİM FORMUL",
  "ISKARTA KG",
  "ISKARTA ADET FORMUL",
  "ISKARTA YÜZDE FORMUL",
  "ISKARTA METRAJ FORMUL",
  "TOPLAM ÜRETİM FORMUL",
  "TOPLAM METRAJ FORMUL",
  "KG/M 2 FORMUL",
  "AKÜMÜLASYON",
  "YIĞMA",
  "FORM DURUŞ",
  "KAYNAK",
  "ELEKTRİK KESİNTİSİ",
  "ELEKTRİK ARIZASI",
  "GALVANİZ ÜNİTESİ",
  "KALİBRE TÜRK KAFALARI",
  "YAĞLAMA",
  "RULMAN DEĞİŞİMİ",
  "TESTERE ARIZASI",
  "MAKARA DEĞİŞİMİ",
  "EBAT AYARI",
  "İSTİF",
  "İŞ EMRİ YOK",
  "PERSONEL EKSİKLİĞİ",
  "YÜKLEME YAPILMASI",
  "KALIP DEĞİŞİMİ",
  "TESTERE DEĞİŞİMİ",
  "TOPLANTI EĞİTİM",
  "SERVİS GEÇ GELMESİ",
  "VİNÇ BEKLEME",
  "DİĞER SEBEPLER",
  "YEMEK ÇAY MOLASI",
  "DURUŞLAR TOPLAMI FORMUL",
  "FİİLİ ÇALIŞMA SÜRESİ",
  "VERİM FORMUL",
];

function hasExcelHeader(text) {
  const firstLine = String(text || "").split(/\r?\n/)[0] || "";
  const upper = firstLine.toLocaleUpperCase("tr-TR");

  return upper.includes("FORM NO") || upper.includes("İŞ EMRİ NO");
}

function buildRawTextForBackend(text) {
  const cleanText = String(text || "").trim();

  if (!cleanText) return "";

  const lines = cleanText
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter(Boolean);

  if (!lines.length) return "";

  const dataLines = hasExcelHeader(cleanText) ? lines.slice(1) : lines;

  return `${DEFAULT_EXCEL_HEADERS.join("\t")}\n${dataLines.join("\n")}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("tr-TR");
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") return value.toLocaleString("tr-TR");
  if (value instanceof Date) return formatDate(value);
  if (typeof value === "boolean") return value ? "Evet" : "Hayır";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function MakinaExcelDebugPage() {
  const [pasteText, setPasteText] = useState("");
  const [pasteMakina, setPasteMakina] = useState("1");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [activeSection, setActiveSection] = useState("missing");

  const summary = result?.summary || null;
  const missingRows = useMemo(() => result?.missingInSystemRows || [], [result]);
  const trulyMissingRows = useMemo(() => result?.trulyMissingRows || [], [result]);
  const sameContentDifferentFormNoDbRows = useMemo(
    () => result?.sameContentDifferentFormNoDbRows || [],
    [result]
  );
  const changedRows = useMemo(() => result?.changedInSystemRows || [], [result]);
  const unchangedRows = useMemo(() => result?.unchangedRows || [], [result]);
  const invalidRows = useMemo(() => result?.invalidRows || [], [result]);
  const localDuplicateRows = useMemo(
    () => result?.localDuplicateRows || [],
    [result]
  );
  const exactExcelDuplicateRows = useMemo(
    () => result?.exactExcelDuplicateRows || [],
    [result]
  );
  const sameContentDifferentFormNoExcelRows = useMemo(
    () => result?.sameContentDifferentFormNoExcelRows || [],
    [result]
  );

  const activeRows = useMemo(() => {
    if (activeSection === "missing") return missingRows;
    if (activeSection === "trueMissing") return trulyMissingRows;
    if (activeSection === "sameContentDb") return sameContentDifferentFormNoDbRows;
    if (activeSection === "changed") return changedRows;
    if (activeSection === "unchanged") return unchangedRows;
    if (activeSection === "invalid") return invalidRows;
    if (activeSection === "exactDuplicate") return exactExcelDuplicateRows;
    if (activeSection === "sameContentExcel")
      return sameContentDifferentFormNoExcelRows;
    return localDuplicateRows;
  }, [
    activeSection,
    missingRows,
    trulyMissingRows,
    sameContentDifferentFormNoDbRows,
    changedRows,
    unchangedRows,
    invalidRows,
    localDuplicateRows,
    exactExcelDuplicateRows,
    sameContentDifferentFormNoExcelRows,
  ]);

  const handleCompare = async () => {
    try {
      setLoading(true);
      setError("");
      setResult(null);

      const rawText = buildRawTextForBackend(pasteText);

      if (!rawText) {
        setError("Lütfen Excel satırlarını yapıştırın.");
        return;
      }

      const res = await compareMakinaExcelDebug({
        makinaAdi: Number(pasteMakina),
        rawText,
      });

      setResult(res.data?.data || null);
    } catch (err) {
      console.error("Excel debug hatası:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          "Excel ve sistem karşılaştırılırken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Excel Debug Karşılaştırma</h2>
          <div className="text-muted small">
            Excelde olup sistemde görünmeyen satırları, alan farklarını ve hatalı kayıtları tek ekranda gösterir.
          </div>
        </div>

        <Form.Select
          value={pasteMakina}
          onChange={(e) => setPasteMakina(e.target.value)}
          style={{ width: 180 }}
        >
          <option value="1">Makina 1</option>
          <option value="2">Makina 2</option>
          <option value="3">Makina 3</option>
          <option value="4">Makina 4</option>
        </Form.Select>
      </div>

      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Form.Control
            as="textarea"
            rows={8}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Excel satırlarını buraya yapıştır."
            style={{ fontFamily: "monospace", fontSize: 13, whiteSpace: "pre" }}
          />

          {error && (
            <Alert variant="danger" className="mt-3 mb-0">
              {error}
            </Alert>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outline-secondary"
              onClick={() => {
                setPasteText("");
                setResult(null);
                setError("");
              }}
              disabled={loading}
            >
              Temizle
            </Button>
            <Button
              variant="primary"
              onClick={handleCompare}
              disabled={loading || !pasteText.trim()}
            >
              {loading ? "Karşılaştırılıyor..." : "Excel ve Sistemi Karşılaştır"}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {loading && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {summary && (
        <>
          <Row className="g-3 mb-4">
            <Col md={2}>
              <Card className="shadow-sm h-100"><Card.Body><div className="text-muted small">Toplam Satır</div><div className="fs-4 fw-bold">{summary.totalRows}</div></Card.Body></Card>
            </Col>
            <Col md={2}>
              <Card className="shadow-sm h-100"><Card.Body><div className="text-muted small">Geçerli</div><div className="fs-4 fw-bold">{summary.validRows}</div></Card.Body></Card>
            </Col>
            <Col md={2}>
              <Card className="shadow-sm h-100 border-danger"><Card.Body><div className="text-muted small">Sistemde Eksik</div><div className="fs-4 fw-bold text-danger">{summary.insertableRows}</div></Card.Body></Card>
            </Col>
            <Col md={2}>
              <Card className="shadow-sm h-100 border-warning"><Card.Body><div className="text-muted small">İçerik Aynı / FormNo Farklı</div><div className="fs-4 fw-bold text-warning">{summary.sameContentDifferentFormNoDbRows}</div></Card.Body></Card>
            </Col>
            <Col md={2}>
              <Card className="shadow-sm h-100 border-success"><Card.Body><div className="text-muted small">Aynı Kayıt</div><div className="fs-4 fw-bold text-success">{summary.unchangedRows}</div></Card.Body></Card>
            </Col>
            <Col md={2}>
              <Card className="shadow-sm h-100 border-secondary"><Card.Body><div className="text-muted small">Excel İçerik Tekrarı</div><div className="fs-4 fw-bold">{summary.exactExcelDuplicateRows + summary.sameContentDifferentFormNoExcelRows}</div></Card.Body></Card>
            </Col>
          </Row>

          <Card className="mb-4 shadow-sm">
            <Card.Body>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button variant={activeSection === "missing" ? "danger" : "outline-danger"} onClick={() => setActiveSection("missing")}>
                  Sistemde Eksik <Badge bg="light" text="dark">{missingRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "trueMissing" ? "outline-danger" : "outline-secondary"} onClick={() => setActiveSection("trueMissing")}>
                  Gerçekten Eksik <Badge bg="light" text="dark">{trulyMissingRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "sameContentDb" ? "warning" : "outline-warning"} onClick={() => setActiveSection("sameContentDb")}>
                  FormNo Farklı Ama Aynı <Badge bg="light" text="dark">{sameContentDifferentFormNoDbRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "changed" ? "warning" : "outline-warning"} onClick={() => setActiveSection("changed")}>
                  Farklı <Badge bg="light" text="dark">{changedRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "unchanged" ? "success" : "outline-success"} onClick={() => setActiveSection("unchanged")}>
                  Aynı <Badge bg="light" text="dark">{unchangedRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "invalid" ? "secondary" : "outline-secondary"} onClick={() => setActiveSection("invalid")}>
                  Hatalı <Badge bg="light" text="dark">{invalidRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "exactDuplicate" ? "dark" : "outline-dark"} onClick={() => setActiveSection("exactDuplicate")}>
                  Birebir Aynı Satır <Badge bg="light" text="dark">{exactExcelDuplicateRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "sameContentExcel" ? "dark" : "outline-dark"} onClick={() => setActiveSection("sameContentExcel")}>
                  Excelde FormNo Farklı Aynı <Badge bg="light" text="dark">{sameContentDifferentFormNoExcelRows.length}</Badge>
                </Button>
                <Button variant={activeSection === "duplicate" ? "dark" : "outline-dark"} onClick={() => setActiveSection("duplicate")}>
                  Excel Tekrarı <Badge bg="light" text="dark">{localDuplicateRows.length}</Badge>
                </Button>
              </div>

              <div className="text-muted small mb-3">
                `FormNo Farklı Ama Aynı` sekmesi tam senin dediğin vakayı bulmak için:
                form numarası değişmiş ama diğer alanlar birebir aynı olan kayıtlar.
              </div>

              <div style={{ overflowX: "auto", maxHeight: "65vh" }}>
                <Table bordered hover responsive className="align-middle mb-0">
                  <thead className="table-light">
                    {activeSection === "changed" ? (
                      <tr>
                        <th>Satır</th>
                        <th>Form No</th>
                        <th>İş Emri</th>
                        <th>Tarih</th>
                        <th>Operatör</th>
                        <th>Farklı Alanlar</th>
                      </tr>
                    ) : activeSection === "sameContentDb" ? (
                      <tr>
                        <th>Satır</th>
                        <th>Excel Form No</th>
                        <th>İş Emri</th>
                        <th>Tarih</th>
                        <th>Eşleşen Sistem Form No</th>
                        <th>Not</th>
                      </tr>
                    ) : activeSection === "exactDuplicate" ||
                      activeSection === "sameContentExcel" ? (
                      <tr>
                        <th>Satır</th>
                        <th>Form No</th>
                        <th>İş Emri</th>
                        <th>Tarih</th>
                        <th>Eşleşen Satırlar</th>
                        <th>Not</th>
                      </tr>
                    ) : activeSection === "invalid" ? (
                      <tr>
                        <th>Satır</th>
                        <th>Hatalar</th>
                        <th>Form No</th>
                        <th>İş Emri</th>
                        <th>Tarih</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Satır</th>
                        <th>Form No</th>
                        <th>İş Emri</th>
                        <th>Tarih</th>
                        <th>Vardiya</th>
                        <th>Operatör</th>
                        <th>Firma</th>
                        <th>Not</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {activeRows.length > 0 ? activeRows.map((row, index) => {
                      if (activeSection === "changed") {
                        return (
                          <tr key={`${row.formNo}-${index}`}>
                            <td>{row.rowNumber}</td>
                            <td>{formatValue(row.formNo)}</td>
                            <td>{formatValue(row.payload?.isEmriNo)}</td>
                            <td>{formatDate(row.payload?.tarih)}</td>
                            <td>{formatValue(row.payload?.operator)}</td>
                            <td>
                              {row.changedFields?.map((field) => (
                                <div key={field.field} className="small mb-1">
                                  <strong>{field.field}</strong>: Sistem {formatValue(field.oldValue)} | Excel {formatValue(field.newValue)}
                                </div>
                              ))}
                            </td>
                          </tr>
                        );
                      }

                      if (activeSection === "sameContentDb") {
                        return (
                          <tr key={`same-db-${row.formNo}-${index}`}>
                            <td>{row.rowNumber}</td>
                            <td>{formatValue(row.formNo)}</td>
                            <td>{formatValue(row.payload?.isEmriNo)}</td>
                            <td>{formatDate(row.payload?.tarih)}</td>
                            <td>
                              {row.matchingDocs?.map((doc) => doc.formNo).join(", ")}
                            </td>
                            <td>{row.reason}</td>
                          </tr>
                        );
                      }

                      if (
                        activeSection === "exactDuplicate" ||
                        activeSection === "sameContentExcel"
                      ) {
                        return (
                          <tr key={`excel-dup-${row.formNo}-${index}`}>
                            <td>{row.rowNumber}</td>
                            <td>{formatValue(row.formNo)}</td>
                            <td>{formatValue(row.payload?.isEmriNo)}</td>
                            <td>{formatDate(row.payload?.tarih)}</td>
                            <td>{row.duplicateRowNumbers?.join(", ")}</td>
                            <td>{row.reason}</td>
                          </tr>
                        );
                      }

                      if (activeSection === "invalid") {
                        return (
                          <tr key={`invalid-${index}`}>
                            <td>{row.rowNumber}</td>
                            <td>{row.errors?.join(", ")}</td>
                            <td>{formatValue(row.normalizedRow?.formNo)}</td>
                            <td>{formatValue(row.normalizedRow?.isEmriNo)}</td>
                            <td>{formatValue(row.normalizedRow?.tarih)}</td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={`${row.formNo || row.payload?.formNo}-${index}`}>
                          <td>{row.rowNumber}</td>
                          <td>{formatValue(row.formNo || row.payload?.formNo)}</td>
                          <td>{formatValue(row.payload?.isEmriNo)}</td>
                          <td>{formatDate(row.payload?.tarih)}</td>
                          <td>{formatValue(row.payload?.vardiya)}</td>
                          <td>{formatValue(row.payload?.operator)}</td>
                          <td>{formatValue(row.payload?.firma)}</td>
                          <td>{row.reason}</td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={activeSection === "changed" || activeSection === "sameContentDb" || activeSection === "exactDuplicate" || activeSection === "sameContentExcel" ? 6 : activeSection === "invalid" ? 5 : 8} className="text-center text-muted py-4">
                          Bu bölümde gösterilecek kayıt yok.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </Layout>
  );
}

export default MakinaExcelDebugPage;
